import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { format, parseISO, isToday, isFuture, isPast } from 'date-fns';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Calendar,
  Clock,
  MapPin,
  Camera,
  LogIn,
  LogOut,
  X,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupervisorMyShiftsPage() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOnShift, setIsOnShift] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [clockingIn, setClockingIn] = useState(false);
  const [allocatedBy, setAllocatedBy] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    fetchMyShifts();
    checkShiftStatus();
  }, []);

  const fetchMyShifts = async () => {
    try {
      const response = await axios.get(`${API}/shifts/my`);
      setShifts(response.data);
    } catch (error) {
      toast.error('Failed to fetch shifts');
    } finally {
      setLoading(false);
    }
  };

  const checkShiftStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setIsOnShift(response.data.is_on_shift || false);
    } catch (error) {
      console.error('Failed to check shift status');
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      setShowCamera(true);
      // NOTE: `srcObject` is attached in the useEffect below (which fires
      // after React commits `showCamera=true` and the <video> element
      // actually mounts). Attaching here would race the render — the ref
      // would still be null on mobile Safari → black preview → black photo.
    } catch (error) {
      toast.error('Unable to access camera. Please allow camera permissions.');
      setShowCamera(false);
    }
  };

  // Deterministic attach + explicit play() so iOS Safari doesn't refuse
  // the fresh MediaStream. Runs whenever the camera modal opens.
  useEffect(() => {
    const v = videoRef.current;
    if (!showCamera || !v || !streamRef.current) return;
    v.srcObject = streamRef.current;
    v.muted = true;
    v.setAttribute('playsinline', 'true');
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        if (err && err.name !== 'AbortError') {
          toast.error('Camera preview blocked. Reload and grant camera access.');
        }
      });
    }
  }, [showCamera]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCapturedPhoto(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    // Same "black photo" guard as EmployeeManualClockInPage: the video
    // stream may still be initialising on first paint; drawImage on a
    // 0×0 canvas silently produces a valid-looking pure-black JPEG.
    if (!video.videoWidth || !video.videoHeight) {
      toast.error('Camera is still starting — try again in a moment.');
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const photoData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedPhoto(photoData);
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
  };

  const validateAllocatedBy = (name) => {
    if (!name || !name.trim()) return false;
    
    const userNameLower = user?.name?.toLowerCase().trim() || '';
    const allocatedLower = name.toLowerCase().trim();
    
    // Check if any part of user's name is in allocated_by
    const userParts = userNameLower.split(' ').filter(p => p.length > 2);
    for (const part of userParts) {
      if (allocatedLower.includes(part)) {
        return false;
      }
    }
    
    // Check if allocated_by parts are in user's name
    const allocatedParts = allocatedLower.split(' ').filter(p => p.length > 2);
    for (const part of allocatedParts) {
      if (userNameLower.includes(part)) {
        return false;
      }
    }
    
    return true;
  };

  const handleClockIn = async () => {
    if (!capturedPhoto) {
      toast.error('Please take a photo before clocking in');
      return;
    }

    // Find today's shift
    const todayShift = shifts.find(s => isToday(parseISO(s.date)));
    
    setClockingIn(true);
    try {
      await axios.post(`${API}/clock/in`, {
        shift_id: todayShift?.id || null,
        photo: capturedPhoto,
        allocated_by: null
      });
      toast.success('Clocked in successfully!');
      setIsOnShift(true);
      setCurrentShift(todayShift);
      setAllocatedBy('');
      stopCamera();
      fetchMyShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    if (!capturedPhoto) {
      toast.error('Please take a photo before clocking out');
      if (!showCamera) {
        startCamera();
      }
      return;
    }
    try {
      const response = await axios.post(`${API}/clock/out`, {
        shift_id: currentShift?.id || null
      });
      // Upload photo
      await axios.post(`${API}/clock/photo`, {
        event_id: response.data.id,
        photo_base64: capturedPhoto
      });
      toast.success('Clocked out successfully!');
      setIsOnShift(false);
      setCurrentShift(null);
      setCapturedPhoto(null);
      stopCamera();
      fetchMyShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  // Group shifts
  const todayShifts = shifts.filter(s => isToday(parseISO(s.date)));
  const upcomingShifts = shifts.filter(s => isFuture(parseISO(s.date)));
  const pastShifts = shifts.filter(s => isPast(parseISO(s.date)) && !isToday(parseISO(s.date)));

  const ShiftCard = ({ shift, variant = 'default' }) => (
    <div className={`p-4 rounded-sm border ${
      variant === 'today' ? 'bg-green-900/20 border-green-800' :
      variant === 'past' ? 'bg-muted/50 border-border opacity-60' :
      'bg-card border-border'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-primary" />
          <span className="font-medium">
            {format(parseISO(shift.date), 'EEE, dd MMM yyyy')}
          </span>
        </div>
        {variant === 'today' && (
          <Badge className="bg-green-600">Today</Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
        <Clock className="w-4 h-4" />
        {shift.start_time && shift.end_time ? (
          <span>{shift.start_time} - {shift.end_time}</span>
        ) : shift.start_time || shift.end_time ? (
          <span>
            {shift.start_time ? (
              <span className="text-foreground">Start {shift.start_time}</span>
            ) : (
              <span className="text-amber-400 font-semibold uppercase tracking-wider">Start Pending</span>
            )}
            <span className="mx-1">•</span>
            {shift.end_time ? (
              <span className="text-foreground">End {shift.end_time}</span>
            ) : (
              <span className="text-amber-400 font-semibold uppercase tracking-wider">End Pending</span>
            )}
          </span>
        ) : (
          <span className="text-amber-400 font-semibold uppercase tracking-wider text-[10px] px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded-sm">Times Pending</span>
        )}
      </div>
      {shift.site_name && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <span>{shift.site_name}</span>
        </div>
      )}
    </div>
  );

  return (
    <SupervisorLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="border-b border-border pb-6">
          <h1 className="text-2xl font-barlow tracking-wider text-primary uppercase flex items-center gap-2">
            <Calendar className="w-6 h-6" />
            My Shifts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            View your assigned shifts and clock in/out
          </p>
        </div>

        {/* Clock In/Out Section */}
        <Card className="bg-card border-border">
          <CardHeader className="border-b border-border pb-3">
            <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Current Status
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Status Display */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Status</p>
                <p className={`text-2xl font-bold ${isOnShift ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {isOnShift ? 'On Duty' : 'Off Duty'}
                </p>
              </div>
              <Badge className={isOnShift ? 'bg-green-600' : 'bg-secondary'}>
                {isOnShift ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            {/* Camera Section for Clock In */}
            {!isOnShift && (
              <>
                {!showCamera ? (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-4">
                      Take a photo to clock in for your shift
                    </p>
                    <Button
                      onClick={startCamera}
                      className="bg-green-600 hover:bg-green-700 text-foreground"
                      data-testid="start-camera-btn"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Open Camera to Clock In
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Camera Preview */}
                    <div className="relative bg-black rounded-sm overflow-hidden aspect-video max-w-md mx-auto">
                      {!capturedPhoto ? (
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src={capturedPhoto}
                          alt="Captured"
                          className="w-full h-full object-cover"
                        />
                      )}
                      <canvas ref={canvasRef} className="hidden" />
                    </div>

                    {/* Camera Controls */}
                    <div className="flex justify-center gap-3">
                      {!capturedPhoto ? (
                        <>
                          <Button
                            onClick={capturePhoto}
                            className="bg-primary hover:bg-primary/90 text-black"
                            data-testid="capture-photo-btn"
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            Take Photo
                          </Button>
                          <Button
                            onClick={stopCamera}
                            variant="outline"
                            className="border-border"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            onClick={handleClockIn}
                            disabled={clockingIn}
                            className="bg-green-600 hover:bg-green-700 text-foreground"
                            data-testid="clock-in-btn"
                          >
                            <LogIn className="w-4 h-4 mr-2" />
                            {clockingIn ? 'Clocking In...' : 'Clock In'}
                          </Button>
                          <Button
                            onClick={retakePhoto}
                            variant="outline"
                            className="border-border"
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            Retake
                          </Button>
                          <Button
                            onClick={stopCamera}
                            variant="outline"
                            className="border-border"
                          >
                            <X className="w-4 h-4 mr-2" />
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Clock Out Button */}
            {isOnShift && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  You are currently on duty
                </p>
                <Button
                  onClick={handleClockOut}
                  className="bg-red-600 hover:bg-red-700 text-foreground"
                  data-testid="clock-out-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Clock Out
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <p className="text-center text-muted-foreground py-8">Loading...</p>
        ) : shifts.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No shifts assigned to you</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Today's Shifts */}
            {todayShifts.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2 text-green-400">
                    <Clock className="w-4 h-4" />
                    Today ({todayShifts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {todayShifts.map(shift => (
                    <ShiftCard key={shift.id} shift={shift} variant="today" />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Upcoming Shifts */}
            {upcomingShifts.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    Upcoming ({upcomingShifts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {upcomingShifts.slice(0, 10).map(shift => (
                    <ShiftCard key={shift.id} shift={shift} />
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Past Shifts */}
            {pastShifts.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader className="border-b border-border pb-3">
                  <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    Past ({pastShifts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  {pastShifts.slice(0, 5).map(shift => (
                    <ShiftCard key={shift.id} shift={shift} variant="past" />
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </SupervisorLayout>
  );
}
